// @flow
import { store } from "common/store";
import { accounting } from "common/utils";
import { watch } from "common/components/watch";

/**
 * Purpose of this file is to extend zoolander getters with UI-specific data, e.g. functions to compute weighted values
 */

export class Getters {
    constructor(zoolanderGetters: $ReadOnlyArray<GetterZoolanderType> = []) {
        this.unsubscribe = store.subscribe(watch(store.getState, "common.mappings.getters", (current, newValue) => current.length === newValue.length)((newGetters) => {
            this.init(newGetters);
        }));
        
        this.init(zoolanderGetters);
    }
    
    destructor() {
        this.unsubscribe();
    }
    
    #pivotIndex: {[string]: GetterType};
    
    #pivotKeyIndex: {[string]: GetterType};
    
    #indexById: {[id: string]: GetterType};
    
    all: $ReadOnlyArray<GetterType>;
    
    static get uiGetters(): $ReadOnlyArray<GetterUIExtensionType> {
        return [
            {
                _id: "5af0f36edcfa183102e8df42", // original_sales_average
                weighted_deps: [
                    "5af0f36edcfa183102e8e00c", // original_sales_amount
                    "5af0f36edcfa183102e8df3f" // sales_count
                ],
                func: (data: Array<Object> | Object) => accounting.sales.averageTicket(data, false)
            },
            
            {
                _id: "5af0f36edcfa183102e8df45", // sales_average
                weighted_deps: [
                    "5af0f36edcfa183102e8e012", // sales_amount
                    "5af0f36edcfa183102e8df3f" // sales_count
                ],
                func: (data: Array<Object> | Object) => accounting.sales.averageTicket(data, true)
            },
    
            {
                _id: "5cc8b6b727e140c6a10dd594", // "Avg. OCT amount (normalized)" oct_average
                weighted_deps: [
                    "5cc8b7b527e140cf0d6e2ba9", // oct_amount
                    "5cc8b7aa27e140ce2961534a" // oct_count
                ],
                func: (data: Array<Object> | Object) => accounting.payouts.averagePayout(data, true)
            },
            {
                _id: "5cc8b6d427e140c70b8cf2c6", // oct_rate_amount
                weighted_deps: [
                    "5cc8b7b527e140cf0d6e2ba9", // oct_amount
                    "5af0f36edcfa183102e8e012" // sales_amount
                ],
                func: (data: Array<Object> | Object) => accounting.payouts.getPayoutRate(data, { by: "amount" })
            },
            {
                _id: "5cc8b6e327e140c766ab6cf5", // oct_rate_count
                weighted_deps: [
                    "5cc8b7aa27e140ce2961534a", // oct_count
                    "5af0f36edcfa183102e8df3f" // sales_count
                ],
                func: (data: Array<Object> | Object) => accounting.payouts.getPayoutRate(data, { by: "count" })
            },
            
            {
                _id: "5af0f36edcfa183102e8df5b", // refunds_rate_amount
                weighted_deps: [
                    "5af0f36edcfa183102e8df53", // refunds_amount
                    "5af0f36edcfa183102e8e012" // sales_amount
                ],
                func: (data: Array<Object> | Object) => accounting.refunds.getRefundRate(data, { by: "amount" })
            },
            
            {
                _id: "5af0f36edcfa183102e8df60", // refunds_rate_count
                weighted_deps: [
                    "5af0f36edcfa183102e8df56", // refunds_count
                    "5af0f36edcfa183102e8df3f" // sales_count
                ],
                func: (data: Array<Object> | Object) => accounting.refunds.getRefundRate(data, { by: "count" })
            },
            
            {
                _id: "5af0f36edcfa183102e8df65", // original_refunds_average
                weighted_deps: [
                    "5af0f36edcfa183102e8df56", // refunds_count
                    "5af0f36edcfa183102e8df48" // original_refunds_amount
                ],
                func: (data: Array<Object> | Object) => accounting.refunds.averageTicket(data, { normalized: false })
            },
            
            {
                _id: "5af0f36edcfa183102e8df6a", // refunds_average
                weighted_deps: [
                    "5af0f36edcfa183102e8df56", // refunds_count
                    "5af0f36edcfa183102e8df53" // refunds_amount
                ],
                func: (data: Array<Object> | Object) => accounting.refunds.averageTicket(data, { normalized: true })
            },
            
            {
                _id: "5af0f36edcfa183102e8df7e", // approval_rate_count
                benchmark_deps: {
                    median: "5c06c2799c172bb18306d514",
                    mean: "5c9e69809c172b527b89961e",
                    rank: "5c06c28d9c172bb2d75f1e9c"
                },
                weighted_deps: [
                    "5af0f36edcfa183102e8df8f", // approvals_count
                    "5af0f36edcfa183102e8df94" // auths_count
                ],
                func: (data: Array<Object> | Object) => accounting.authorizations.approvalRate(data, { by: "count" })
            },
            
            {
                _id: "5af0f36edcfa183102e8df81", // approval_rate_amount
                benchmark_deps: {
                    median: "5c06c2849c172bb297aa6808",
                    mean: "5c9e69759c172b5212df3171",
                    rank: "5c06c2959c172bb321afa8eb"
                },
                weighted_deps: [
                    "5af0f36edcfa183102e8df8a", // approvals_amount
                    "5af0f36edcfa183102e8df9c" // auths_amount
                ],
                func: (data: Array<Object> | Object) => accounting.authorizations.approvalRate(data, { by: "amount" })
            },
            
            {
                _id: "5af0f36edcfa183102e8dfa1", // original_auths_average
                weighted_deps: [
                    "5af0f36edcfa183102e8df98", // original_auths_amount
                    "5af0f36edcfa183102e8df94" // auths_count
                ],
                func: (data: Array<Object> | Object) => accounting.authorizations.averageAuthTicket(data, { normalized: false })
            },
            
            {
                _id: "5af0f36edcfa183102e8dfa5", // auths_average
                weighted_deps: [
                    "5af0f36edcfa183102e8df9c", // auths_amount
                    "5af0f36edcfa183102e8df94" // auths_count
                ],
                func: (data: Array<Object> | Object) => accounting.authorizations.averageAuthTicket(data, { normalized: true })
            },
            
            {
                _id: "5bf320a7d63f0237b4a5fcea", // approvals_average
                weighted_deps: [
                    "5af0f36edcfa183102e8df8a", // approvals_amount
                    "5af0f36edcfa183102e8df8f" // approvals_count
                ],
                func: (data: Array<Object> | Object) => accounting.authorizations.averageApprovalTicket(data, { normalized: true })
            },
            
            {
                _id: "5bf320a7d63f0237b4a5fce9", // original_approvals_average
                weighted_deps: [
                    "5af0f36edcfa183102e8df86", // original_approvals_amount
                    "5af0f36edcfa183102e8df8f" // approvals_count
                ],
                func: (data: Array<Object> | Object) => accounting.authorizations.averageApprovalTicket(data, { normalized: false })
            },
            
            {
                _id: "5af0f36edcfa183102e8dfaa", // decline_rate_count
                weighted_deps: [
                    "5af0f36edcfa183102e8dfb3", // declines_count
                    "5af0f36edcfa183102e8df94" // auths_count
                ],
                func: (data: Array<Object> | Object) => accounting.authorizations.declineRate(data, { by: "count" })
            },
            
            {
                _id: "5af0f36edcfa183102e8dfaf", // decline_rate_amount
                weighted_deps: [
                    "5af0f36edcfa183102e8dfd6", // declines_amount
                    "5af0f36edcfa183102e8df9c" // auths_amount
                ],
                func: (data: Array<Object> | Object) => accounting.authorizations.declineRate(data, { by: "amount" })
            },
            
            {
                _id: "5af0f36edcfa183102e8dfe9", // original_declines_average
                weighted_deps: [
                    "5af0f36edcfa183102e8dfc5", // original_declines_amount
                    "5af0f36edcfa183102e8dfb3" // declines_count
                ],
                func: (data: Array<Object> | Object) => accounting.authorizations.averageDeclineTicket(data, {
                    by: "amount",
                    normalized: false
                })
            },
            
            {
                _id: "5af0f36edcfa183102e8dfee", // original_hard_declines_average
                weighted_deps: [
                    "5af0f36edcfa183102e8dfc8", // original_hard_declines_amount
                    "5af0f36edcfa183102e8dfb8" // hard_declines_count
                ],
                func: (data: Array<Object> | Object) => accounting.authorizations.averageDeclineTicket(data, {
                    by: "amount",
                    declineType: "hard",
                    normalized: false
                })
            },
            
            {
                _id: "5af0f36edcfa183102e8dff2", // original_soft_declines_average
                weighted_deps: [
                    "5af0f36edcfa183102e8dfcd", // original_soft_declines_amount
                    "5af0f36edcfa183102e8dfbc" // soft_declines_count
                ],
                func: (data: Array<Object> | Object) => accounting.authorizations.averageDeclineTicket(data, {
                    by: "amount",
                    declineType: "soft",
                    normalized: false
                })
            },
            
            {
                _id: "5af0f36edcfa183102e8dff6", // original_reject_declines_average
                weighted_deps: [
                    "5af0f36edcfa183102e8dfd2", // original_reject_declines_amount
                    "5af0f36edcfa183102e8dfc0" // reject_declines_count
                ],
                func: (data: Array<Object> | Object) => accounting.authorizations.averageDeclineTicket(data, {
                    by: "amount",
                    declineType: "reject",
                    normalized: false
                })
            },
            
            {
                _id: "5af0f36edcfa183102e8dffb", // declines_average
                weighted_deps: [
                    "5af0f36edcfa183102e8dfd6", // declines_amount
                    "5af0f36edcfa183102e8dfb3" // declines_count
                ],
                func: (data: Array<Object> | Object) => accounting.authorizations.averageDeclineTicket(data, {
                    by: "amount",
                    normalized: true
                })
            },
            
            {
                _id: "5af0f36edcfa183102e8dfff", // hard_declines_average
                weighted_deps: [
                    "5af0f36edcfa183102e8dfdb", // hard_declines_amount
                    "5af0f36edcfa183102e8dfb8" // hard_declines_count
                ],
                func: (data: Array<Object> | Object) => accounting.authorizations.averageDeclineTicket(data, {
                    by: "amount",
                    declineType: "hard",
                    normalized: true
                })
            },
            
            {
                _id: "5af0f36edcfa183102e8e004", // soft_declines_average
                weighted_deps: [
                    "5af0f36edcfa183102e8dfe0", // soft_declines_amount
                    "5af0f36edcfa183102e8dfbc" // soft_declines_count
                ],
                func: (data: Array<Object> | Object) => accounting.authorizations.averageDeclineTicket(data, {
                    by: "amount",
                    declineType: "soft",
                    normalized: true
                })
            },
            
            {
                _id: "5af0f36edcfa183102e8e007", // reject_declines_average
                weighted_deps: [
                    "5af0f36edcfa183102e8dfe4", // reject_declines_amount
                    "5af0f36edcfa183102e8dfc0" // reject_declines_count
                ],
                func: (data: Array<Object> | Object) => accounting.authorizations.averageDeclineTicket(data, {
                    by: "amount",
                    declineType: "reject",
                    normalized: true
                })
            },
            
            {
                _id: "5af0f36edcfa183102e8e036", // chargebacks_rate
                weighted_deps: [
                    "5af0f36edcfa183102e8df3f", // sales_count
                    "5af0f36edcfa183102e8e028" // chargebacks_count
                ],
                func: (data: Array<Object> | Object) => accounting.chargebacks.chargebackRate(data)
            },
            
            {
                _id: "5af0f36edcfa183102e8e03a", // original_chargebacks_average
                weighted_deps: [
                    "5af0f36edcfa183102e8e02c", // original_chargebacks_amount
                    "5af0f36edcfa183102e8e028" // chargebacks_count
                ],
                func: (data: Array<Object> | Object) => accounting.chargebacks.averageTicket(data, {
                    normalized: false
                })
            },
            
            {
                _id: "5af0f36edcfa183102e8e03f", // chargebacks_average
                weighted_deps: [
                    "5af0f36edcfa183102e8e031", // chargebacks_amount
                    "5af0f36edcfa183102e8e028" // chargebacks_count
                ],
                func: (data: Array<Object> | Object) => accounting.chargebacks.averageTicket(data, {
                    normalized: true
                })
            },
            
            {
                _id: "5af0f36edcfa183102e8e05d", // effective_fee_rate
                weighted_deps: [
                    "5af0f36edcfa183102e8e044", // fees_amount
                    "5af0f36edcfa183102e8df3d" // sales_amount
                ],
                func: (data: Array<Object> | Object) => accounting.fees.grossEffectiveRate(data)
            },
            
            {
                _id: "5bb3b54b9c172bb19aab3ad7", // net_effective_fee_rate
                weighted_deps: [
                    "5af0f36edcfa183102e8e044", // fees_amount
                    "5af0f36edcfa183102e8df3d", // sales_amount
                    "5af0f36edcfa183102e8df53" // refunds_amount
                ],
                func: (data: Array<Object> | Object) => accounting.fees.netEffectiveRate(data)
            },
            
            {
                _id: "5af34effdd57b3e5d458d236", // auths_percent_of_total_count
                benchmark: true,
                weighted_deps: [
                    "5af0f36edcfa183102e8df94", // auths_count
                    "5af0f36edcfa183102e8dfb3" // declines_count
                ],
                func: (data: Array<Object> | Object, all: Array<Object>) => {
                    const reducer = (memo, p) => memo + (p.auths_count || p.declines_count);
                    return accounting.getRate({
                        value: data.reduce(reducer, 0),
                        total: all.reduce(reducer, 0),
                        precision: 2,
                        humanize: false
                    });
                }
            },
            
            {
                _id: "5af34effdd57b3e5d458d237", // auths_percent_of_total_amount
                benchmark: true,
                weighted_deps: [
                    "5af0f36edcfa183102e8dfd6", // declines_amount
                    "5af0f36edcfa183102e8df9c" // auths_amount
                ],
                func: (data: Array<Object> | Object, all: Array<Object>) => {
                    const reducer = (memo, p) => memo + (p.auths_amount || p.declines_amount);
                    return accounting.getRate({
                        value: data.reduce(reducer, 0),
                        total: all.reduce(reducer, 0),
                        precision: 2,
                        humanize: false
                    });
                }
            },
            
            {
                _id: "5be49e5526ef682ec9ea8932", // fraud_rate_purchase
                weighted_deps: [
                    "5be1e41517b044396fdd8e9b", // fraud_purchase_amount
                    "5af0f36edcfa183102e8df3a" // sales_purcahse_amount
                ],
                func: (data: Array<Object> | Object) => accounting.fraud.fraudPurchaseRate(data)
            },
            
            {
                _id: "5be49e5526ef682ec9ea8933", // fraud_rate_stl
                weighted_deps: [
                    "5be1e41517b044396fdd8e9c", // fraud_stl_amount
                    "5af0f36edcfa183102e8df3d" // sales_amount
                ],
                func: (data: Array<Object> | Object) => accounting.fraud.fraudStlRate(data)
            },
            
            {
                _id: "5c06c2959c172bb321afa8eb", // rank_approval_rate_amount
                // $FlowFixMe Flow will error here, but won't fix for now because we should move to weights soon
                func: (data: Object) => data.rank_approval_rate_amount
            },
            
            {
                _id: "5c06c28d9c172bb2d75f1e9c", // rank_approval_rate_count
                // $FlowFixMe Flow will error here, but won't fix for now because we should move to weights soon
                func: (data: Object) => data.rank_approval_rate_count
            },
            
            {
                _id: "5c06c2849c172bb297aa6808", // median_approval_rate_amount
                // $FlowFixMe Flow will error here, but won't fix for now because we should move to weights soon
                func: (data: Object) => data.median_approval_rate_amount
            },
            
            {
                _id: "5c9e69759c172b5212df3171", // mean_approval_rate_amount
                // $FlowFixMe Flow will error here, but won't fix for now because we should move to weights soon
                func: (data: Object) => data.mean_approval_rate_amount
            },
            
            {
                _id: "5c06c2799c172bb18306d514", // median_approval_rate_count
                // $FlowFixMe Flow will error here, but won't fix for now because we should move to weights soon
                func: (data: Object) => data.median_approval_rate_count
            },
            
            {
                _id: "5c9e69809c172b527b89961e", // mean_approval_rate_count
                // $FlowFixMe Flow will error here, but won't fix for now because we should move to weights soon
                func: (data: Object) => data.mean_approval_rate_count
            },
            
            {
                _id: "5b19997edabe69133a95ef89", // fees_and_taxes_amount
                decimals: 6
            },
            
            {
                _id: "5b19997edabe69133a95ef85", // amount
                decimals: 6
            },
    
            {
                _id: "5b19997edabe69133a95ef8b", // transaction cost
                decimals: 6
            },
    
            {
                _id: "5b19997edabe69133a95ef86", // Rate (R)
                decimals: 6
            },
    
            {
                _id: "5b19997edabe69133a95efa6", // fx_rate
                decimals: 6
            },

            {
                _id: "5b19997edabe69133a95ef75", // provider
                pivot: true
            },

            {
                _id: "5b19997edabe69133a95ef77", // processor_status (response type)
                pivot: true
            },

            {
                _id: "5ba900139c172b8bd98ad7e2", // "authentication_method",
                key: "auth_method",
                pivot: true
            },

            {
                _id: "5ba9000c9c172b8bbd6c9a52", // "transaction_entry_method",
                pivot: true
            },

            {
                _id: "5ba900049c172b8bad63e128", // "terminal_type",
                pivot: true
            },
            
            {
                _id: "5b19997edabe69133a95ef81", // merchant_id
                pivot: true
            },
            
            {
                _id: "6022b63b1351298f210a71b7", // average_cashback_amount
                weighted_deps: [
                    "5f3572f7bd3630533ee2efdc", // cashback_amount
                    "6022b63b1351298f210a71b4" // cashback_count
                ],
                func: (data: Array<Object> | Object) => accounting.sales.getAverageCashBack(data, { normalized: true })
            },
    
            {
                _id: "6022b63b1351298f210a71b8", // original_average_cashback_amount
                weighted_deps: [
                    "6022b63b1351298f210a71b5", // original_cashback_amount
                    "6022b63b1351298f210a71b4" // cashback_count
                ],
                func: (data: Array<Object> | Object) => accounting.sales.getAverageCashBack(data, { normalized: false })
            },
    
            {
                _id: "6022b63b1351298f210a71b9", // average_order_amount
                weighted_deps: [
                    "5f3572f7bd3630533ee2efdb", // order_amount
                    "5af0f36edcfa183102e8df3f" // sales_count
                ],
                func: (data: Array<Object> | Object) => accounting.sales.getAverageOrder(data, { normalized: true })
            },
    
            {
                _id: "6022b63b1351298f210a71ba", // original_average_order_amount
                weighted_deps: [
                    "6022b63b1351298f210a71b6", // original_order_amount
                    "5af0f36edcfa183102e8df3f" // sales_count
                ],
                func: (data: Array<Object> | Object) => accounting.sales.getAverageOrder(data, { normalized: false })
            },
            
            // Need to add this later to the db
            {
                _id: "mid",
                pivot: true,
                key: "mid",
                name: "MID",
                name_plural: "MIDs"
            }
        ];
    }
    
    static categoryToApiResource = {
        authorizations: "authorizations",
        declines: "authorizations",
        cashflow: "cashflow",
        chargebacks: "chargebacks",
        fees: "fees",
        fees_details: "fees",
        hard_declines: "authorizations",
        refunds: "settlements",
        payouts: "settlements",
        sales: "settlements",
        reject_declines: "authorizations",
        response_code_declines: "authorizations",
        soft_declines: "authorizations",
        fraud: "fraud",
        psd2: "psd2"
    };
    
    static merge(zooArr: $ReadOnlyArray<GetterZoolanderType>, uiArr: $ReadOnlyArray<GetterUIExtensionType>): $ReadOnlyArray<GetterType> {
        // $FlowFixMe Flow cannot really understand how we're merging two Types together here
        return [...zooArr, ...uiArr].reduce((a: Array<GetterType>, b: GetterZoolanderType | GetterUIExtensionType) => {
            // Find a match based on all keys in mergeKeys
            const match = a.find(({ _id }) => _id === b._id);
            if (match) {
                Object.assign(match, b);
            } else {
                a.push(b);
            }
            
            return a;
        }, []);
    }
    
    init(zoolanderGetters) {
        this.all = Getters.merge(zoolanderGetters, Getters.uiGetters);
        this.#indexById = this.all.reduce((acc, g: GetterType) => ({
            ...acc,
            [g._id]: g
        }), {});
        this.#pivotIndex = this.all.reduce((acc, g: GetterType) => {
            if (g.pivot) {
                return {
                    ...acc,
                    [g.pivot_alias || g.key]: g
                };
            }
            return acc;
        }, {});
    
        this.#pivotKeyIndex = this.all.reduce((acc, g: GetterType) => {
            if (g.pivot) {
                return {
                    ...acc,
                    [g.key]: g
                };
            }
            return acc;
        }, {});
    }
    
    groupByApiResource(filter: (GetterType) => boolean = (getter) => true) {
        return this.all.reduce((memo, cur) => {
            // We're only interested in analytics categories here and omitting txns, etc.
            if (Getters.categoryToApiResource[cur.category] && filter(cur)) {
                return {
                    ...memo,
                    [Getters.categoryToApiResource[cur.category]]: [
                        ...memo[Getters.categoryToApiResource[cur.category]] || [],
                        cur
                    ]
                };
            }
            
            return memo;
        }, {});
    }
    
    findById(id: string): ?GetterType {
        return this.#indexById[id];
    }
    
    find(): ?GetterType {
        return Array.prototype.find.apply(this.all, arguments);
    }
    
    reduce() {
        return Array.prototype.reduce.apply(this.all, arguments);
    }
    
    filter(): Array<GetterType> {
        return Array.prototype.filter.apply(this.all, arguments);
    }
    
    /**
     * This method iterates through a list of provided getters and sort them accordingly to arbitrary sort order
     */
    reduceAndSortGetters(predicateFn: (GetterType) => boolean = () => true, sortOrder: Array<string> = []): Array<GetterType> {
        // Convert sortOrder to object for faster lookups
        const sortOrderObj = sortOrder.reduce((memo: Object, s: string, idx: number) => {
            memo[s] = idx;
            return memo;
        }, {});
        
        return this.all.reduce((memo: Array<GetterType>, g: GetterType): Array<GetterType> => {
            if (predicateFn(g)) {
                const hideOnLoad = !Object.prototype.hasOwnProperty.call(sortOrderObj, g.key);
                if (hideOnLoad) {
                    sortOrderObj[g.key] = "-1";
                }
                
                // $FlowFixMe Flow doesn't how to properly extend an object https://github.com/facebook/flow/issues/1805
                memo.push({
                    ...g,
                    pivotTableHideOnInitialLoad: hideOnLoad
                });
            }
            
            return memo;
        }, []).sort((a: GetterType, b: GetterType): number => sortOrderObj[a.key] - sortOrderObj[b.key]);
    }
    
    allPivots() {
        // Return a clone to prevent mutation
        return {
            ...this.#pivotIndex
        };
    }
    
    // Finds a getter either by a pivot name or a key
    findPivot(pivot) {
        const match = this.#pivotIndex[pivot];
        
        if (typeof match === "undefined") {
            return this.#pivotKeyIndex[pivot];
        }
        
        return match;
    }
    
    findPivotName(pivot, { plural = false } = {}) {
        const { name = "", name_plural = "" } = this.findPivot(pivot) || {};
        
        let match = name;
        if (plural) {
            match = name_plural;
        }
        
        return match;
    }
    
    findPivotKey(pivot) {
        const match = this.findPivot(pivot);
        
        if (!match) {
            throw new Error(`Cannot find a key for requested pivot: ${pivot}`);
        }
        
        return match.key;
    }
    
    findPivotFromKey(key) {
        const match = this.#pivotKeyIndex[key];
        
        if (!match) {
            throw new Error(`Unable to get pivot for key ${key}`);
        }
        
        return match.pivot_alias || key;
    }
    
    includesPivot(pivot) {
        return typeof this.findPivot(pivot) !== "undefined";
    }
    
    getCustomPivots() {
        return Object.entries(this.#pivotIndex).reduce((acc, [pivot, getter]) => {
            if (getter.pivot_tag) {
                return [...acc, pivot];
            }
            return acc;
        }, []);
    }
}

export const getters = new Getters();
